import * as React from "react";
import PropTypes from "prop-types";
import { withABTestWrapper } from "@lululemon/mwa-ab-testing";
import LinkGenerator from "@lululemon/ecom-common/src/lib/link-generator";

import { LinkProvider, baseRenderContentType } from "@lululemon/ui-library";
import { ContentfulClient } from "@lululemon/cosy-contentful-client";
import { renderContentfulComponent } from "@lululemon/cosy-contentful-renderer";

import classNames from "classnames";
import { compose } from "@lululemon/mwa-utils";
import { withErrorBoundary } from "@lululemon/mwa-common";
import { isUserLoggedIn } from "@lululemon/mwa-auth";
import { useLaunchDarklyFlag } from "@lululemon/mwa-launch-darkly";
import { getPromoTileToShowFromContentfulGridItem } from "../product-list/product-list-util";
import { publicConfig } from "@lululemon/ecom-common/src/util/public-config";
import { loyaltyEngineCallback } from "@lululemon/mwa-loyalty";

const PromoTileLinkGenerator = ({ href, ...rest }) => (
  <LinkGenerator href={href} data-component-link="promo_tile" {...rest} />
);

PromoTileLinkGenerator.propTypes = {
  ...LinkGenerator.propTypes,
};

const PromoTile = ({
  containerClass,
  gridItemData,
  locale,
  truefitAtCDPABFlag,
}) => {
  const contentfulPromoData = getPromoTileToShowFromContentfulGridItem({
    contentfulGridItem: gridItemData,
    isUserLoggedIn: isUserLoggedIn(),
  });
  const isSingleTile = gridItemData?.fields?.layout === "singleTile";

  const loyaltyEngineEnabled = useLaunchDarklyFlag(
    "enable-loyalty-engine-on-cdp-promo-tile",
    false,
    publicConfig?.launchDarkly?.loyaltyEngineClientSideId
  );
  /* needed for every content model which is not in renderContentType */
  const emptyFn = () => {};

  return (
    <LinkProvider component={PromoTileLinkGenerator}>
      {contentfulPromoData ? (
        <div
          className={classNames(
            containerClass,
            "col-xs-12",
            isSingleTile ? "col-sm-4" : "col-sm-8"
          )}
          data-testid="promo-tile"
        >
          {renderContentfulComponent({
            contentfulComponent: contentfulPromoData,
            options: {
              loyaltyEngineConfig: {
                enabled: loyaltyEngineEnabled,
                ldClientId:
                  publicConfig?.launchDarkly?.loyaltyEngineClientSideId,
                contentfulAccessToken: publicConfig?.contentful?.accessToken,
                contentfulPreviewToken: publicConfig?.contentful?.previewToken,
                ContentfulClient,
                locale: locale?.replace("_", "-") || "en-US",
                graphqlUrl: `${publicConfig?.apiHost}/api/graphql`,
              },
              callback: loyaltyEngineCallback,
              renderContentType: {
                ...baseRenderContentType,
                webLoyaltyEngineInterface: emptyFn,
                webMetaAnalyticsParameters: emptyFn,
              },
              transformElement: ({ element, contentType }) => {
                if (
                  contentType === "webPromoCard" &&
                  element.props?.ctaURL?.includes("/story/bra-quiz")
                ) {
                  return React.cloneElement(element, {
                    ctaProps: {
                      className: truefitAtCDPABFlag
                        ? "tfc-fitrec-register"
                        : "",
                      "data-category": truefitAtCDPABFlag ? "Bras" : null,
                      "data-locale": truefitAtCDPABFlag ? "en_US" : null,
                      tag: truefitAtCDPABFlag ? "div" : "a",
                    },
                    ctaURL: truefitAtCDPABFlag ? "#" : element.props.ctaURL,
                  });
                }

                return element;
              },
            },
          })}
        </div>
      ) : null}
    </LinkProvider>
  );
};

PromoTile.propTypes = {
  containerClass: PropTypes.string,
  gridItemData: PropTypes.object,
  index: PropTypes.number, // used by withABTestWrapper hoc
  truefitAtCDPABFlag: PropTypes.bool,
};

PromoTile.defaultProps = {
  containerClass: undefined,
  gridItemData: undefined,
  index: 0,
  truefitAtCDPABFlag: false,
};

export { PromoTile }; // for testing

const ComposedPromoTile = compose(
  withABTestWrapper({
    name: "promo-tile",
    mapPropsToName: ({ index }, name) =>
      index != null ? `${name}-${index}` : name,
    abTestEndDate: "06/01/2023",
  }),
  withErrorBoundary({ componentName: "Promo Tile" })
)(PromoTile);

ComposedPromoTile.displayName = "ComposedPromoTile";

export default ComposedPromoTile;
